import { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLiveQuery } from 'dexie-react-hooks';
import { offlineDB, type PendingBonCarburant, type PendingVoyage } from './offlineDB';
import { useOnlineStatus } from './useOnlineStatus';
import { useAuthStore } from '@/stores/authStore';
import { transportApi } from '@/api/transport';
import { extractError } from '@/api/client';

export interface PendingCounts {
  voyages:       number;
  bonsCarburant: number;
  total:         number;
  errors:        number;
}

/**
 * Hook : sync auto au retour de connexion + sync manuel.
 * Lit le compteur live via dexie-react-hooks (useLiveQuery), filtré par filialeKey.
 * Au retour online → déclenche sync auto.
 */
export function useOfflineSync(): {
  counts:    PendingCounts;
  online:    boolean;
  syncing:   boolean;
  syncNow:   () => Promise<void>;
  pendingVoyages:       { id: number; preview: any; syncStatus: string; errorMsg?: string; createdAt: string }[];
  pendingBonsCarburant: { id: number; preview: any; syncStatus: string; errorMsg?: string; createdAt: string }[];
} {
  const online = useOnlineStatus();
  const qc     = useQueryClient();
  const filialeKey = useAuthStore((s) => s.filiale?.filialeKey);
  const [syncing, setSyncing] = useState(false);

  // Live counts depuis Dexie (réactif)
  const allVoyages: PendingVoyage[] = useLiveQuery(
    () => filialeKey ? offlineDB.voyages.where('filialeKey').equals(filialeKey).toArray() : Promise.resolve([] as PendingVoyage[]),
    [filialeKey],
  ) ?? [];
  const allBons: PendingBonCarburant[] = useLiveQuery(
    () => filialeKey ? offlineDB.bonsCarburant.where('filialeKey').equals(filialeKey).toArray() : Promise.resolve([] as PendingBonCarburant[]),
    [filialeKey],
  ) ?? [];

  const counts: PendingCounts = {
    voyages:       allVoyages.filter((v) => v.syncStatus !== 'synced').length,
    bonsCarburant: allBons.filter((b) => b.syncStatus !== 'synced').length,
    total:         0,
    errors:        0,
  };
  counts.total  = counts.voyages + counts.bonsCarburant;
  counts.errors = allVoyages.filter((v) => v.syncStatus === 'error').length
                + allBons.filter((b) => b.syncStatus === 'error').length;

  const pendingVoyages = allVoyages
    .filter((v) => v.syncStatus !== 'synced')
    .map((v) => ({ id: v.id!, preview: v.preview, syncStatus: v.syncStatus, errorMsg: v.errorMsg, createdAt: v.createdAt }));
  const pendingBonsCarburant = allBons
    .filter((b) => b.syncStatus !== 'synced')
    .map((b) => ({ id: b.id!, preview: b.preview, syncStatus: b.syncStatus, errorMsg: b.errorMsg, createdAt: b.createdAt }));

  const syncNow = useCallback(async () => {
    if (!filialeKey) return;
    if (syncing) return;
    setSyncing(true);
    try {
      // Voyages
      const voyages = await offlineDB.voyages
        .where('filialeKey').equals(filialeKey)
        .filter((v) => v.syncStatus === 'pending' || v.syncStatus === 'error')
        .toArray();
      for (const v of voyages) {
        if (!v.id) continue;
        await offlineDB.voyages.update(v.id, { syncStatus: 'syncing', errorMsg: undefined });
        try {
          await transportApi.createVoyage(v.payload);
          await offlineDB.voyages.update(v.id, { syncStatus: 'synced' });
        } catch (err) {
          await offlineDB.voyages.update(v.id, { syncStatus: 'error', errorMsg: extractError(err).message });
        }
      }
      // Bons carburant
      const bons = await offlineDB.bonsCarburant
        .where('filialeKey').equals(filialeKey)
        .filter((b) => b.syncStatus === 'pending' || b.syncStatus === 'error')
        .toArray();
      for (const b of bons) {
        if (!b.id) continue;
        await offlineDB.bonsCarburant.update(b.id, { syncStatus: 'syncing', errorMsg: undefined });
        try {
          await transportApi.createBonCarburant(b.payload);
          await offlineDB.bonsCarburant.update(b.id, { syncStatus: 'synced' });
        } catch (err) {
          await offlineDB.bonsCarburant.update(b.id, { syncStatus: 'error', errorMsg: extractError(err).message });
        }
      }
      // Invalide les caches React Query pour refresh listes
      qc.invalidateQueries({ queryKey: ['operations', 'transport'] });
    } finally {
      setSyncing(false);
    }
  }, [filialeKey, syncing, qc]);

  // Sync auto au retour de connexion
  useEffect(() => {
    if (online && counts.total > 0 && !syncing) {
      void syncNow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  return { counts, online, syncing, syncNow, pendingVoyages, pendingBonsCarburant };
}
